import React, { useState, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';

// --- Components ---
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import { Card, Collapse, IconButton, CircularProgress, Tabs, Tab, Grid, Divider } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { grey } from '@mui/material/colors';
import InfoIcon from '@mui/icons-material/Info';

// --- Contexts ---
import { useVisibility } from 'context/visibilityContext';

// --- Helper function for formatting field names ---
const formatFieldName = (fieldName) => {
  return fieldName
    .replace('SBQQ__', '')
    .replace('__c', '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

// --- FieldDisplay component (no changes needed) ---
function FieldDisplay({ fields }) {
  const fieldEntries = Object.entries(fields || {}).filter(
    ([, value]) => value !== null && value !== undefined && value !== ''
  );

  if (fieldEntries.length === 0) {
    return (
      <ArgonTypography variant="caption" color="text" fontStyle="italic">
        No configured fields to display.
      </ArgonTypography>
    );
  }

  return (
    <ArgonBox>
      {fieldEntries.map(([key, value]) => (
        <ArgonBox key={key} mb={1} width="100%">
          <Grid container alignItems="center" spacing={0}>
            <Grid item xs={7}>
              <ArgonTypography
                variant="caption"
                fontSize={13}
                fontWeight="medium"
                color="textSecondary"
                align="left"
                sx={{ wordWrap: 'break-word', color: grey[700], ml: 3 }}
              >
                {formatFieldName(key)}
              </ArgonTypography>
            </Grid>
            <Grid item xs={1} sx={{ textAlign: 'center' }}>
              <Divider orientation="vertical" flexItem />
            </Grid>
            <Grid item xs={3} sx={{ textAlign: 'center' }}>
              <ArgonBox
                bgcolor={grey[200]}
                color={grey[700]}
                p={1}
                borderRadius="md"
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{ width: '100%', boxSizing: 'border-box', minHeight: '36px' }}
              >
                <ArgonTypography
                  variant="caption"
                  fontWeight="bold"
                  sx={{ wordWrap: 'break-word', width: '100%', textAlign: 'center' }}
                >
                  {String(value)}
                </ArgonTypography>
              </ArgonBox>
            </Grid>
          </Grid>
        </ArgonBox>
      ))}
    </ArgonBox>
  );
}
FieldDisplay.propTypes = { fields: PropTypes.object };

// --- Helper function to filter fields based on visibility config ---
const getVisibleFields = (allFields, objectName, visibilityConfig, orgType) => {
  // `orgType` will be 'cpq' or 'rc'
  const configSection = visibilityConfig[orgType] || []; // Select the correct section
  const config = configSection.find((c) => c.objectName === objectName);

  if (!config || !config.displayColumns) {
    console.warn(`Visibility config missing 'displayColumns' for object: ${objectName}`);
    return {};
  }

  const allowedFields = new Set(config.displayColumns.split(',').map((f) => f.trim()));
  return Object.fromEntries(Object.entries(allFields || {}).filter(([key]) => allowedFields.has(key)));
};

// --- Sub-component for each template to manage its own state ---
function TemplateDetail({ template, orgType }) {
  const { visibilityConfig } = useVisibility();
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => setActiveTab(newValue);

  const { name: templateName } = template.quoteTemplate;

  // Use useMemo to prevent re-filtering on every render
  const visibleQuoteFields = useMemo(
    () => getVisibleFields(template.quoteTemplate.fields, 'SBQQ__QuoteTemplate__c', visibilityConfig, orgType),
    [template.quoteTemplate.fields, visibilityConfig, orgType]
  );

  return (
    <Card sx={{ mb: 1.5, overflow: 'hidden' }}>
      <ArgonBox
        p={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        onClick={() => setIsExpanded(!isExpanded)}
        sx={{ cursor: 'pointer' }}
      >
        <ArgonTypography variant="body2" fontWeight="medium" sx={{ color: grey[600] }}>
          {templateName}
        </ArgonTypography>
        <IconButton aria-expanded={isExpanded} aria-label="expand" size="small">
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </ArgonBox>
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <ArgonBox sx={{ borderTop: `1px solid ${grey[300]}` }}>
          <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
            <Tab label="General Settings" />
            <Tab label="Template Sections" />
            <Tab label="Line Columns" />
          </Tabs>
          <ArgonBox p={2}>
            {activeTab === 0 && <FieldDisplay fields={visibleQuoteFields} />}
            {activeTab === 1 && (
              <ArgonBox>
                {template.templateSections.length > 0 ? (
                  template.templateSections.map((section, index) => {
                    const visibleSectionFields = getVisibleFields(
                      section.templateSections.fields,
                      'SBQQ__TemplateSection__c',
                      visibilityConfig
                    );
                    return (
                      <ArgonBox key={section.templateSections.id || index} mb={3}>
                        <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                          {`${section.templateSections.name}`}
                        </ArgonTypography>
                        <FieldDisplay fields={visibleSectionFields} />
                        {index !== template.templateSections.length - 1 ? <Divider /> : <></>}
                      </ArgonBox>
                    );
                  })
                ) : (
                  <ArgonTypography variant="body2">No template sections defined.</ArgonTypography>
                )}
              </ArgonBox>
            )}
            {activeTab === 2 && (
              <ArgonBox>
                {template.lineColumns.length > 0 ? (
                  template.lineColumns.map((column, index) => {
                    const visibleColumnFields = getVisibleFields(
                      column.fields,
                      'SBQQ__LineColumn__c',
                      visibilityConfig
                    );
                    return (
                      <ArgonBox key={column.id || index} mb={3}>
                        <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                          {`${column.name}`}
                        </ArgonTypography>
                        <FieldDisplay fields={visibleColumnFields} />
                        {index !== template.lineColumns.length - 1 ? <Divider /> : <></>}
                      </ArgonBox>
                    );
                  })
                ) : (
                  <ArgonTypography variant="body2">No line columns defined.</ArgonTypography>
                )}
              </ArgonBox>
            )}
          </ArgonBox>
        </ArgonBox>
      </Collapse>
    </Card>
  );
}
TemplateDetail.propTypes = { template: PropTypes.object.isRequired };

// --- Main Quote Component ---
const lifecycleSettingsFields = {
  'Expiration Policy': '30 days',
  'Auto-Renew': 'Disabled',
  'Default Subscription Term': '12 months',
  'Default Start Date Behavior': 'Start date = Quote creation date',
};

function Quote({ quoteTemplates, loadingTemplates, selectedOrgId, loading, orgType }) {
  const [lifecycleExpanded, setLifecycleExpanded] = useState(false);
  const [templatesExpanded, setTemplatesExpanded] = useState(true);
  const [localisationExpanded, setLocalisationExpanded] = useState(false);

  const renderTemplatesSection = () => {
    if (loading) {
      return (
        <ArgonBox display="flex" justifyContent="center" alignItems="center" p={3}>
          <CircularProgress size={24} />
          <ArgonTypography variant="body2" sx={{ ml: 2 }}>
            Loading...
          </ArgonTypography>
        </ArgonBox>
      );
    }

    if (!selectedOrgId) {
      return (
        <ArgonBox p={4} textAlign="center" display="flex" flexDirection="column" alignItems="center">
          <InfoIcon color="info" sx={{ fontSize: 40, mb: 1 }} />
          <ArgonTypography variant="h6" color="text" fontWeight="medium">
            Please select a CPQ Organization to view templates.
          </ArgonTypography>
        </ArgonBox>
      );
    }

    if (loadingTemplates) {
      return (
        <ArgonBox display="flex" justifyContent="center" alignItems="center" p={3}>
          <CircularProgress size={24} />
          <ArgonTypography variant="body2" sx={{ ml: 2 }}>
            Loading Quote Templates...
          </ArgonTypography>
        </ArgonBox>
      );
    }

    if (!quoteTemplates || quoteTemplates.length === 0) {
      return (
        <ArgonBox p={3} pt={0} textAlign="center">
          <ArgonTypography variant="body2" color="text">
            No quote templates found for this organization.
          </ArgonTypography>
        </ArgonBox>
      );
    }

    return (
      <ArgonBox p={2} pt={1}>
        {quoteTemplates.map((template) => (
          <TemplateDetail key={template.quoteTemplate.id} template={template} orgType={orgType} /> // Pass orgType
        ))}
      </ArgonBox>
    );
  };

  return (
    <ArgonBox>
      {/* Cards remain the same */}
      <Card sx={{ mb: 2, boxShadow: 'md' }}>
        <ArgonBox
          p={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          onClick={() => setLifecycleExpanded(!lifecycleExpanded)}
          sx={{ cursor: 'pointer' }}
        >
          <ArgonTypography variant="body2" fontWeight="bold" color="secondary">
            1. Quote Lifecycle Settings
          </ArgonTypography>
          <IconButton aria-expanded={lifecycleExpanded} aria-label="expand" size="small">
            {lifecycleExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </ArgonBox>
        <Collapse in={lifecycleExpanded} timeout="auto" unmountOnExit>
          <ArgonBox p={3} pt={0}>
            {Object.entries(lifecycleSettingsFields).map(([key, value]) => (
              <ArgonBox key={key} mb={1} width="100%">
                <Grid container alignItems="center" spacing={0}>
                  <Grid item xs={7}>
                    <ArgonTypography
                      variant="caption"
                      fontSize={13}
                      fontWeight="medium"
                      color="textSecondary"
                      align="left"
                      sx={{ wordWrap: 'break-word', color: grey[700], ml: 3 }}
                    >
                      {key}
                    </ArgonTypography>
                  </Grid>
                  <Grid item xs={1} sx={{ textAlign: 'center' }}>
                    <Divider orientation="vertical" flexItem />
                  </Grid>
                  <Grid item xs={3} sx={{ textAlign: 'center' }}>
                    <ArgonBox
                      bgcolor={grey[200]}
                      color={grey[700]}
                      p={1}
                      borderRadius="md"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={{ width: '100%', boxSizing: 'border-box', minHeight: '36px' }}
                    >
                      <ArgonTypography
                        variant="caption"
                        fontWeight="bold"
                        sx={{ wordWrap: 'break-word', width: '100%', textAlign: 'center' }}
                      >
                        {value || '-'}
                      </ArgonTypography>
                    </ArgonBox>
                  </Grid>
                </Grid>
              </ArgonBox>
            ))}
          </ArgonBox>
        </Collapse>
      </Card>

      <Card sx={{ mb: 2, boxShadow: 'md' }}>
        <ArgonBox
          p={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          onClick={() => setTemplatesExpanded(!templatesExpanded)}
          sx={{ cursor: 'pointer' }}
        >
          <ArgonTypography variant="body2" fontWeight="bold" color="secondary">
            2. Quote Templates
          </ArgonTypography>
          <IconButton aria-expanded={templatesExpanded} aria-label="expand" size="small">
            {templatesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </ArgonBox>
        <Collapse in={templatesExpanded} timeout="auto" unmountOnExit>
          {renderTemplatesSection()}
        </Collapse>
      </Card>

      <Card sx={{ mb: 2, boxShadow: 'md' }}>
        <ArgonBox
          p={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          onClick={() => setLocalisationExpanded(!localisationExpanded)}
          sx={{ cursor: 'pointer' }}
        >
          <ArgonTypography variant="body2" fontWeight="bold" color="secondary">
            3. Localisation
          </ArgonTypography>
          <IconButton aria-expanded={localisationExpanded} aria-label="expand" size="small">
            {localisationExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </ArgonBox>
        <Collapse in={localisationExpanded} timeout="auto" unmountOnExit>
          <ArgonBox p={3} pt={0} textAlign="center">
            <ArgonTypography variant="body2" color="text">
              No localisation configuration.
            </ArgonTypography>
          </ArgonBox>
        </Collapse>
      </Card>
    </ArgonBox>
  );
}

Quote.propTypes = {
  quoteTemplates: PropTypes.array,
  loadingTemplates: PropTypes.bool,
  selectedOrgId: PropTypes.string,
  loading: PropTypes.bool,
};

export default Quote;
